import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import './App.css';
import { getBasePath, getLoginPath } from './utils/pathUtils';

// Layout Components
import Sidebar from './components/Layout/Sidebar';
import Header from './components/Layout/Header';
import AppFooter from './components/Layout/AppFooter';

// Page Components
import Dashboard from './components/Dashboard/Dashboard';
import PMDashboard from './components/Dashboard/PMDashboard';
import VisitList from './components/Visits/VisitList';
import VisitManagement from './components/Visits/VisitManagement';
import UnlockTransactionList from './components/Visits/UnlockTransactionList';
import VisitSchedulingPlanner from './components/Visits/VisitSchedulingPlanner';
import VisitForm from './components/Visits/VisitForm';
import VisitDetail from './components/Visits/VisitDetail';
import VisitEmailAction from './components/Visits/VisitEmailAction';
import ReportsHome from './components/Reports/ReportsHome';
import ReportList from './components/Reports/ReportList';
import ReportForm from './components/Reports/ReportForm';
import ReportDetail from './components/Reports/ReportDetail';
import ActionItemList from './components/ActionItems/ActionItemList';
import ActionItemDetail from './components/ActionItems/ActionItemDetail';
import SiteList from './components/Sites/SiteList';
import SiteFeasibilitySelection from './components/Sites/SiteFeasibilitySelection';
import StsLpCsvImport from './components/Sites/StsLpCsvImport';
import AuditTrail from './components/Audit/AuditTrail';
import AuditReport from './components/Reports/AuditReport';
import DCFReport from './components/Reports/DCFReport';
import ActionItemReport from './components/Reports/ActionItemReport';
import CRFDataReport from './components/Reports/CRFDataReport';
import CRAVisitReport from './components/Reports/CRAVisitReport';
import LoginLogoutReport from './components/Reports/LoginLogoutReport';
import ReportOperationRoute from './components/Reports/ReportOperationRoute';
import CRFPage from './components/CRF/CRFPage';
import CRFManagement from './components/CRF/CRFManagement';
import CRFForm from './components/CRF/CRFForm';
import TestData from './components/TestData/TestData';
import StudyConfigurationPage from './studyConfiguration/StudyConfigurationPage';
import MailManagement from './components/MailManagement/MailManagement';
import VisitLetterDownloads from './components/VisitLetters/VisitLetterDownloads';
import Login from './components/Auth/Login';
import LoginError from './components/Auth/LoginError';
import ByPassLogin from './components/Auth/ByPassLogin';

// Context & Utils
import api, { clearClientStorage, SESSION_EXPIRED_EVENT, SESSION_EXPIRED_MESSAGE } from './services/api';
import { AuthContext } from './context/AuthContext';
import { MenuProvider } from './context/MenuContext';
import { NotificationContext } from './context/NotificationContext';
import { SessionProvider, useSession } from './context/SessionContext';
import { ProjectProvider } from './context/ProjectContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import Notification from './components/Common/Notification';
import Alert from './components/Common/Alert';

const normalizeSessionUser = (rawUser) => {
  if (!rawUser || typeof rawUser !== 'object') return rawUser;
  const profileCode = (
    rawUser.profileCode
    || rawUser.ProfileCode
    || rawUser.profile
    || rawUser.Profile
    || rawUser.vUserTypeCode
    || rawUser.role
    || rawUser.Role
    || ''
  ).toString().trim();
  const profileDescription = (
    rawUser.profileDescription
    || rawUser.ProfileDescription
    || rawUser.vProfileDescription
    || ''
  ).toString().trim();

  return {
    ...rawUser,
    profileCode,
    profileDescription,
  };
};

// When on /login with ?error=, show dedicated error page instead of login form
function LoginOrErrorPlaceholder() {
  const [searchParams] = useSearchParams();
  return searchParams.get('error') ? <LoginError /> : <Login />;
}

// Redirect to login with returnUrl so after login user returns to the same page (e.g. visit approve/reject)
function NavigateToLoginWithReturn() {
  const location = useLocation();
  const path = location.pathname + location.search;
  const loginPath = getLoginPath();
  const to = path ? `${loginPath}?returnUrl=${encodeURIComponent(path)}` : loginPath;
  return <Navigate to={to} replace />;
}

const SIDEBAR_OPEN_KEY = 'ctms-sidebar-open';

function getInitialSidebarOpen() {
  try {
    const stored = localStorage.getItem(SIDEBAR_OPEN_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
  } catch (_) {
    /* ignore */
  }
  if (typeof window !== 'undefined' && window.matchMedia('(min-width: 769px)').matches) {
    return true;
  }
  return false;
}

// Inner component to access SessionContext
function AppContent() {
  const { initializeAuditValues, updateAuditValue, clearAuditValues } = useSession();
  const [user, setUser] = useState(null);
  const [sessionProject, setSessionProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(getInitialSidebarOpen);
  const [sessionExpiredToast, setSessionExpiredToast] = useState(false);

  useEffect(() => {
    const onSessionExpired = () => setSessionExpiredToast(true);
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_OPEN_KEY, String(next));
      } catch (_) {
        /* ignore */
      }
      return next;
    });
  };

  // Session is checked only once on app load. If URL has login error param (e.g. from Gateway
  // validation redirect), clear any existing session and do NOT bind menus — show error page only.
  useEffect(() => {
    let cancelled = false;
    const hasLoginErrorInUrl = () => {
      const path = (typeof window !== 'undefined' && window.location.pathname) || '';
      const search = (typeof window !== 'undefined' && window.location.search) || '';
      const base = getBasePath();
      const pathWithoutBase = base ? (path.slice(base.length) || '/') : path;
      return pathWithoutBase === '/login-error' || (pathWithoutBase === '/login' && search.includes('error='));
    };
    const restoreSession = async () => {
      if (hasLoginErrorInUrl()) {
        try {
          await api.post('/Auth/Logout');
        } catch (_) {
          // ignore
        }
        clearClientStorage();
        if (!cancelled) {
          setUser(null);
          setSessionProject(null);
          setLoading(false);
        }
        return;
      }
      let sessionUserLoaded = false;
      try {
        const res = await api.get('/Auth/CheckSession');
        if (cancelled) return;
        if (res.data?.user) {
          sessionUserLoaded = true;
          setUser(normalizeSessionUser(res.data.user));
          initializeAuditValues({
            ...(res.data.recordedByMappingId != null && res.data.recordedByMappingId !== undefined ? { nRecordedBy: res.data.recordedByMappingId } : {}),
            ...(res.data.recordedSign != null && res.data.recordedSign !== '' ? { vRecordedSign: res.data.recordedSign } : {}),
            ...(res.data.user?.timeZoneNo != null ? { nRecordedAtTimeZone: res.data.user.timeZoneNo } : {}),
            ...(res.data.user?.timeZoneOffset != null ? { vRecordedAtOffset: res.data.user.timeZoneOffset } : {}),
          });
          if (res.data.project) {
            setSessionProject(res.data.project);
            // Call SetProject once when session is bound so server has correct recordedByMappingId for this project
            try {
              const setProjRes = await api.post('/Auth/SetProject', res.data.project);
              if (setProjRes.data?.recordedByMappingId != null) {
                updateAuditValue('nRecordedBy', setProjRes.data.recordedByMappingId);
              }
            } catch (_) { /* ignore */ }
          }
        }
      } catch (err) {
        if (!cancelled) {
          setUser(null);
          setSessionProject(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          // After shell + ProjectProvider mount, refresh header notification badge (PI / overdue) with correct project scope.
          if (sessionUserLoaded && typeof window !== 'undefined') {
            window.setTimeout(() => window.dispatchEvent(new Event('projectUpdated')), 0);
          }
        }
      }
    };
    restoreSession();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    const loginFullUrl = `${window.location.origin}${getLoginPath()}`;
    let logoutRedirectUrl = loginFullUrl;
    try {
      const res = await api.post('/Auth/Logout');
      if (res.data?.clearClientStorage) clearClientStorage();
      if (res.data?.logoutRedirectUrl) {
        const url = res.data.logoutRedirectUrl.trim();
        const basePath = getBasePath() || '';
        logoutRedirectUrl = url.startsWith('http') ? url : `${window.location.origin}${basePath}${url.startsWith('/') ? url : '/' + url}`;
      }
    } catch (e) {
      clearClientStorage();
    }
    setUser(null);
    setSessionProject(null);
    clearAuditValues();
    window.location.href = logoutRedirectUrl;
  };

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ user, setUser, sessionProject, setSessionProject, handleLogout }}>
      <NotificationContext.Provider value={{ notification, setNotification }}>
        {user ? (
          <MenuProvider>
            <ProjectProvider initialProject={sessionProject}>
              <div className="app-layout">
                <div className="app-layout-body">
                  <Sidebar isOpen={sidebarOpen} />
                  <div className="main-content">
                    <Header user={user} onLogout={handleLogout} onToggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />
                    <div className="content-area">
                      <Routes>
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/pm-dashboard" element={<PMDashboard />} />

                        {/* Visit Routes */}
                        <Route path="/unlock-transactions" element={<UnlockTransactionList />} />
                        <Route path="/visits/unlock-transactions" element={<Navigate to="/unlock-transactions" replace />} />
                        <Route path="/visits" element={<VisitManagement />} />
                        <Route path="/planner" element={<VisitSchedulingPlanner />} />
                        <Route path="/visits/list" element={<VisitList />} />
                        <Route path="/visits/new" element={<VisitForm />} />
                        <Route path="/visits/:id" element={<VisitDetail />} />
                        <Route path="/visits/:id/edit" element={<VisitForm />} />
                        <Route path="/visit/:id/approve" element={<VisitEmailAction action="approve" />} />
                        <Route path="/visit/:id/reject" element={<VisitEmailAction action="reject" />} />
                        <Route path="/followup/:id/approve" element={<VisitEmailAction action="approve" flow="followup" />} />
                        <Route path="/followup/:id/reject" element={<VisitEmailAction action="reject" flow="followup" />} />

                        {/* Report Routes */}
                        <Route path="/reports" element={<ReportsHome />} />
                        <Route path="/reports/visit-execution" element={<ReportList />} />
                        <Route path="/reports/new" element={<ReportForm />} />
                        <Route path="/reports/:id" element={<ReportDetail />} />
                        <Route path="/reports/:id/edit" element={<ReportForm />} />

                        {/* Action Item Routes */}
                        <Route path="/action-items" element={<ActionItemList />} />
                        <Route path="/action-items/:id" element={<ActionItemDetail />} />

                        {/* Site Routes */}
                        <Route path="/sites" element={<SiteList />} />
                        <Route path="/sitefeasible" element={<SiteFeasibilitySelection />} />
                        <Route path="/sitefeasible/sts-lp-import" element={<StsLpCsvImport />} />

                        {/* Mail Management */}
                        <Route path="/mail-management" element={<MailManagement />} />

                        {/* Visit Letter Downloads */}
                        <Route path="/visit-letters" element={<VisitLetterDownloads />} />

                        {/* Audit Routes */}
                        <Route path="/audit" element={<AuditTrail />} />
                        <Route path="/reports/audit" element={<ReportOperationRoute operationPath="/reports/audit"><AuditReport /></ReportOperationRoute>} />
                        <Route path="/reports/dcf" element={<ReportOperationRoute operationPath="/reports/dcf"><DCFReport /></ReportOperationRoute>} />
                        <Route path="/reports/action-item" element={<ReportOperationRoute operationPath="/reports/action-item"><ActionItemReport /></ReportOperationRoute>} />
                        <Route path="/reports/crf-data" element={<ReportOperationRoute operationPath="/reports/crf-data"><CRFDataReport /></ReportOperationRoute>} />
                        <Route path="/reports/cra-visit" element={<ReportOperationRoute operationPath="/reports/cra-visit"><CRAVisitReport /></ReportOperationRoute>} />
                        <Route path="/reports/login-logout" element={<ReportOperationRoute operationPath="/reports/login-logout"><LoginLogoutReport /></ReportOperationRoute>} />

                        {/* CRF Routes */}
                        <Route path="/crf" element={<CRFPage />} />
                        <Route path="/crf/management" element={<CRFManagement />} />
                        <Route path="/crf/template/:templateId" element={<CRFForm />} />
                        <Route path="/crf/template/:templateId/visit/:visitId" element={<CRFForm />} />
                        <Route path="/crf/form/:templateId" element={<CRFForm />} />

                        {/* Study Configuration */}
                        <Route path="/configuration" element={<StudyConfigurationPage />} />

                        {/* Test / Dev */}
                        <Route path="/test-data" element={<TestData />} />

                        {/* Default redirect */}
                        <Route path="/" element={<Navigate to="/dashboard" />} />
                      </Routes>
                    </div>
                  </div>
                </div>
                <AppFooter />
              </div>
            </ProjectProvider>
          </MenuProvider>
        ) : (
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login-error" element={<LoginError />} />
            <Route path="/login" element={<LoginOrErrorPlaceholder />} />
            {/* Bypass login entry point from external system */}
            <Route path="/bypass-login" element={<ByPassLogin />} />
            <Route path="/visit/:id/approve" element={<VisitEmailAction action="approve" />} />
            <Route path="/visit/:id/reject" element={<VisitEmailAction action="reject" />} />
            <Route path="/followup/:id/approve" element={<VisitEmailAction action="approve" flow="followup" />} />
            <Route path="/followup/:id/reject" element={<VisitEmailAction action="reject" flow="followup" />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        )}
        {notification && <Notification notification={notification} />}
        {sessionExpiredToast && (
          <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
            <Alert type="error" message={SESSION_EXPIRED_MESSAGE} asToaster autoClose={false} onClose={() => setSessionExpiredToast(false)} />
          </div>
        )}
      </NotificationContext.Provider>
    </AuthContext.Provider>
  );
}

function App() {
  return (
    <SessionProvider>
      <AppContent />
    </SessionProvider>
  );
}

export default App;